// Supplier photos: a thumbnail (desktop hover enlarges it, tap opens the gallery) and a compact manage modal for admins.
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaImages } from 'react-icons/fa';
import api from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { translateKnown } from '../../i18n/knownMessages';
import InfoModal from './InfoModal';
import PhotoViewer from './PhotoViewer';

export const MAX_GALLERY = 4;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // matches the backend limit
const TOO_LARGE_MESSAGE = 'Image exceeds the 5 MB limit.';
const SERVER_LIMIT_MESSAGE = 'Server upload limit exceeded. Try a smaller image or contact support.';
const UPLOAD_FAILED_MESSAGE = 'Upload failed. Please try again.';
const BASE = process.env.REACT_APP_API_URL || '';
// Absolute URLs (R2 public base) are used as they are; stored keys resolve against the R2 base when set, else the local uploads
const R2_BASE = process.env.REACT_APP_R2_PUBLIC_URL ? process.env.REACT_APP_R2_PUBLIC_URL.replace(/\/$/, '') : null;
export const imageSrc = (key) => {
  if (!key) return null;
  if (/^https?:\/\//.test(key)) return key;
  return R2_BASE ? `${R2_BASE}/${key}` : `${BASE}/uploads/${key}`;
};

export default function SupplierImages({ supplierId, images, canManage = false, compact = false, onChange, endpoint = `/api/suppliers/${supplierId}/images`, galleryTitle = 'Supplier photos', variant = 'table', maxGallery = MAX_GALLERY }) {
  const { t } = useTranslation();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(''); // shown inline next to the upload controls
  const [preview, setPreview] = useState(null); // { left, top } of the floating hover preview (viewport px)
  const wrapRef = useRef(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const isTouch = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches;
  // Desktop 240px; mobile/tablet min(180px, 45vw)
  const previewPx = isTouch ? Math.min(180, window.innerWidth * 0.45) : 240;
  const computePreview = (el) => {
    const r = el.getBoundingClientRect();
    // Right of the thumbnail if it fits, otherwise left; always inside the viewport
    const rightOk = r.right + 8 + previewPx <= window.innerWidth - 8;
    const left = Math.max(8, rightOk ? r.right + 8 : r.left - previewPx - 8);
    const top = Math.min(Math.max(8, r.top), window.innerHeight - previewPx - 8);
    return { left, top };
  };
  const showPreview = (e) => setPreview(computePreview(e.currentTarget));
  // Touch: tap the thumbnail to open/close the preview; desktop click still opens the gallery
  const onThumbClick = (e) => {
    if (!isTouch) { setPhotoIndex(0); return setGalleryOpen(true); }
    // Read the element now; the event's currentTarget is gone by the time a state updater runs
    setPreview(preview ? null : computePreview(e.currentTarget));
  };
  // Touch: an outside tap closes the preview
  useEffect(() => {
    if (!preview) return undefined;
    const onOutside = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setPreview(null); };
    document.addEventListener('touchstart', onOutside);
    document.addEventListener('mousedown', onOutside);
    return () => {
      document.removeEventListener('touchstart', onOutside);
      document.removeEventListener('mousedown', onOutside);
    };
  }, [preview]);
  const primary = images?.primary || null;
  const gallery = images?.gallery || [];
  const all = [primary, ...gallery].filter(Boolean);
  const size = variant === 'info' ? 'w-full max-h-48' : compact ? 'h-12 w-12' : 'h-24 w-24';

  const upload = async (slot, file) => {
    if (!file) return;
    setUploadError('');
    // Quick check before sending; the server still validates the file and has the final say
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(TOO_LARGE_MESSAGE);
      return;
    }
    const form = new FormData();
    form.append('image', file);
    form.append('slot', slot);
    setBusy(true);
    try {
      const res = await api.post(endpoint, form);
      onChange?.(res.data);
    } catch (err) {
      // 413 comes from the proxy in front of the API. Only a backend validation message (400, plain text) is shown;
      // HTML bodies, stack traces and server errors get the generic text
      const status = err.response?.status;
      const message = status === 413 ? SERVER_LIMIT_MESSAGE
        : status === 400 && typeof err.response?.data?.message === 'string' ? err.response.data.message
        : UPLOAD_FAILED_MESSAGE;
      setUploadError(message);
      toast.error(translateKnown(t, message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (key) => {
    setBusy(true);
    try {
      const res = await api.delete(endpoint, { data: { key } });
      onChange?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || t('dialogs.photos.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'info' && !primary) return null;
  if (!primary && !canManage) return <span className="text-gray-400">-</span>;

  return (
    <div className="flex flex-col gap-1">
      {primary ? (
        <div className={variant === 'info' ? 'w-full' : 'w-fit relative'} ref={wrapRef}>
          <button
            type="button"
            onClick={variant === 'info' ? () => { setPhotoIndex(0); setGalleryOpen(true); } : onThumbClick}
            onMouseEnter={(e) => { if (!isTouch) showPreview(e); }}
            onMouseLeave={() => { if (!isTouch) setPreview(null); }}
            aria-label={t('dialogs.photos.openPhotos')}
            className="block focus:outline-none focus:ring-2 focus:ring-green-600 rounded"
          >
            <img src={imageSrc(primary)} alt="" className={`${size} object-cover rounded border`} />
            {variant === 'info' && <span className="block mt-1 text-blue-600 hover:underline text-sm">{t('dialogs.photos.viewPhotos', { count: all.length })}</span>}
          </button>
          {/* Mobile: compact overlay opens the photos modal (not the preview) */}
          {isTouch && variant !== 'info' && (
            <button
              type="button"
              onClick={() => { setPreview(null); setPhotoIndex(0); setGalleryOpen(true); }}
              className="absolute bottom-0 left-0 right-0 flex items-center justify-center bg-black/60 text-white text-xs py-0.5 rounded-b"
            >
              <FaImages />
              <span className="sr-only">{t('dialogs.photos.viewPhotosSr')}</span>
            </button>
          )}
          {/* Floating desktop preview, fixed to the viewport so it never changes table layout */}
          {preview && variant !== 'info' && (
            <img
              src={imageSrc(primary)}
              alt=""
              style={{ position: 'fixed', left: preview.left, top: preview.top, width: previewPx, height: previewPx, objectFit: 'contain', background: 'white', zIndex: 60, pointerEvents: 'none' }}
              className="rounded border shadow-lg"
            />
          )}
        </div>
      ) : (
        canManage && <span className="text-xs text-gray-500">{t('dialogs.photos.noPhotos')}</span>
      )}

      {canManage && (
        <>
          <span className="text-xs text-gray-600">{all.length}/{maxGallery + 1}</span>
          <button type="button" onClick={() => { setUploadError(''); setManageOpen(true); }} className="text-xs text-blue-600 hover:underline text-left">
            {all.length ? t('dialogs.photos.managePhotos') : t('dialogs.photos.addPhotos')}
          </button>
        </>
      )}

      <InfoModal isOpen={manageOpen} onClose={() => { setManageOpen(false); setUploadError(''); }} title={t('dialogs.photos.managePhotos')}>
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-center gap-3">
            {primary ? <img src={imageSrc(primary)} alt="" className="h-12 w-12 object-cover rounded border" /> : <span className="text-gray-500">{t('dialogs.photos.noPrimary')}</span>}
            <label className="text-blue-600 cursor-pointer hover:underline">
              {primary ? t('dialogs.photos.replacePrimary') : t('dialogs.photos.addPrimary')}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" disabled={busy} onChange={(e) => upload('primary', e.target.files[0])} />
            </label>
            {primary && (
              <button type="button" disabled={busy} onClick={() => remove(primary)} className="text-red-600 hover:underline">{t('dialogs.photos.deletePrimary')}</button>
            )}
          </div>
          {uploadError && <p role="alert" className="text-red-600">{translateKnown(t, uploadError)}</p>}
          <div>
            <label className={`cursor-pointer hover:underline ${gallery.length >= maxGallery ? 'text-gray-400 pointer-events-none' : 'text-blue-600'}`}>
              {t('dialogs.photos.addPhotoCount', { count: gallery.length, max: maxGallery })}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" disabled={busy || gallery.length >= maxGallery} onChange={(e) => upload('gallery', e.target.files[0])} />
            </label>
          </div>
          {gallery.length > 0 && (
            <ul className="space-y-2">
              {gallery.map((key) => (
                <li key={key} className="flex items-center gap-3">
                  <img src={imageSrc(key)} alt="" className="h-12 w-12 object-cover rounded border" />
                  <button type="button" disabled={busy} onClick={() => remove(key)} className="text-red-600 hover:underline">{t('dialogs.photos.deletePhoto')}</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </InfoModal>

      {galleryOpen && all.length > 0 && (
        <PhotoViewer photos={all} index={Math.min(photoIndex, all.length - 1)} onIndex={setPhotoIndex} onClose={() => setGalleryOpen(false)} />
      )}
    </div>
  );
}
